import { createApp } from 'vue'
import * as Sentry from '@sentry/vue'
import App from './App.vue'
import { i18n } from './i18n'
import { isExternalObjectUpdateRejection, unknownErrorMessage } from './utils/errors'
import {
  isInstagramHandlingDisabledRejection,
  isRuntimeSendMessageTabNotFoundRejection,
  sentryExceptionMessages,
} from './utils/sentryFilters'
import './assets/main.css'

// Global error handler for stale custom scripts (branding scripts from old tours)
// that reference undefined globals (e.g., zaloJSV2). Reloads the page to re-fetch fresh assets.
if (typeof window !== 'undefined') {
  window.onerror = (msg, src, line, col, err) => {
    const message = unknownErrorMessage(err || msg, '')
    const isRefError = message.includes("Can't find variable") || message.includes('is not defined')
    const isChunkError = message.includes('Failed to fetch dynamically imported module')

    if (isRefError || isChunkError) {
      // Avoid reload loops
      const reloadKey = 'viewer_error_reload'
      if (!sessionStorage.getItem(reloadKey)) {
        sessionStorage.setItem(reloadKey, '1')
        console.warn('[VTE] Stale asset or missing variable error, reloading...', msg)
        window.location.reload()
      }
    }
    // Let Sentry handle reporting
    return false
  }

  window.addEventListener('unhandledrejection', (event) => {
    if (isExternalObjectUpdateRejection(event.reason)) {
      event.preventDefault()
    }
  })
}

const app = createApp(App)

// Sentry Error Tracking
if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_ENVIRONMENT || 'development',
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: import.meta.env.VITE_ENVIRONMENT === 'production' ? 0.2 : 0,
    replaysOnErrorSampleRate: import.meta.env.VITE_ENVIRONMENT === 'production' ? 1.0 : 0,
    beforeSend(event, hint) {
      const exceptionMessages = sentryExceptionMessages(event)

      if (
        isExternalObjectUpdateRejection(hint?.originalException) ||
        isExternalObjectUpdateRejection(event.message) ||
        isExternalObjectUpdateRejection(exceptionMessages)
      ) {
        return null
      }

      if (isInstagramHandlingDisabledRejection(event, hint)) return null
      if (isRuntimeSendMessageTabNotFoundRejection(event, hint)) return null

      const err = hint?.originalException
      if (err instanceof Error) {
        // Fullscreen / XR blocked by iframe permissions policy — not a bug
        if (err.message?.includes('Fullscreen API is disabled by permissions policy')) return null
        if (err.message?.includes('xr-spatial-tracking')) return null
        if (err.message?.includes('permissions policy')) return null
        // Stale chunk / module load failure — handled by auto-reload
        if (err.message?.includes('Failed to fetch dynamically imported module')) return null
        if (err.message?.includes('Importing a module script failed')) return null
        // User custom scripts (e.g. Zalo SDK) failing — not our code
        if (err.message?.includes("Can't find variable")) return null
      }
      return event
    },
  })
}

app.use(i18n)
app.mount('#app')

// Initialize locale after mounting to avoid circular dependency issues
import { initializeLocale } from './i18n'
initializeLocale()
